import { IconButton } from "@dropbox/dig-components/buttons";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronDownLine, ChevronUpLine } from "@dropbox/dig-icons/assets";
import { Title } from "@mirage/mosaics/BaseProfilePage/Title";
import { useEffect, useRef, useState } from "react";

const useSectionHeight = (ref: React.RefObject<HTMLDivElement>) => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (ref.current) {
        setHeight(ref.current.scrollHeight);
      }
    });

    resizeObserver.observe(ref.current);

    return () => resizeObserver.disconnect();
  }, [ref]);

  return height;
};

export const CollapsibleSectionTitle = ({
  sectionTitle,
  isOpen,
  setIsOpen,
}: {
  sectionTitle: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  return (
    <Box as="div" display="flex" alignItems="center" style={{ gap: "4px" }}>
      <Title size="small" weightVariant="emphasized" tagName="span">
        {sectionTitle}
      </Title>
      <IconButton
        variant="transparent"
        size="small"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <UIIcon src={ChevronUpLine} />
        ) : (
          <UIIcon src={ChevronDownLine} />
        )}
      </IconButton>
    </Box>
  );
};

export const CollapsibleSection = ({
  isOpen,
  children,
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) => {
  const transitionDuration = 375;
  const ref = useRef<HTMLDivElement>(null);
  const height = useSectionHeight(ref);

  // Avoid animation on first render
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setHasMounted(true), 0);
    return () => clearTimeout(t);
  }, []);

  // When collapsing a section, add padding to bottom of page to
  // keep the section title in place
  useEffect(() => {
    const container = document.querySelector(
      "#user-profile-page-container",
    ) as HTMLElement | null;
    const sectionEl = ref.current;

    if (!hasMounted || !isOpen || !container || !sectionEl) return;

    const sectionHeight = sectionEl.scrollHeight;
    container.style.paddingBottom = `${sectionHeight}px`;

    return () => {
      // Remove padding when component unmounts
      const container = document.querySelector(
        "#user-profile-page-container",
      ) as HTMLElement | null;

      if (container) {
        container.style.paddingBottom = "";
      }
    };
  }, [isOpen]);

  const opacityTransition = isOpen
    ? `opacity 100ms ease-in-out ${transitionDuration * 0.5}ms` // delay on expand
    : "opacity 100ms ease-in-out"; // no delay on collapse

  return (
    <Box
      style={{
        maxHeight: isOpen ? height : 0,
        opacity: isOpen ? 1 : 0,
        overflow: isOpen ? "visible" : "hidden",
        transition: hasMounted
          ? `max-height ${transitionDuration}ms cubic-bezier(0.17, 0, 0.09, 1), ${opacityTransition}`
          : undefined,
      }}
    >
      <Box as="div" ref={ref}>
        {children}
      </Box>
    </Box>
  );
};
